import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { BankIcon } from '@/ui/Icons/BankIcon';
import { LayerTwoIcon } from '@/ui/Icons/LayerTwoIcon';
import { Multiselect, MultiselectItem } from '@/ui/Multiselect';

import { DiscoveryFilters, DiscoveryPreset } from '@/utils/discovery/types';
import { fetcher } from '@/utils/fetcher';
import { routes } from '@/utils/routes';

type BackingFilterProps = {
  filters: DiscoveryFilters;
  onSelectionComplete: (backings?: string[]) => void;
  preset: DiscoveryPreset;
};

const toLabel = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

const defaultLabel = { default: 'Backing', entity: 'backings', Icon: BankIcon };
const commoditiesLabel = { default: 'Underlying Asset', entity: 'assets', Icon: LayerTwoIcon };

export const BackingFilter = ({ filters, onSelectionComplete, preset }: BackingFilterProps) => {
  const [selectedBackings, setSelectedBackings] = useState(filters.backings);

  const { data: backings, isLoading } = useQuery<string[] | undefined, Error>({
    queryKey: ['discovery', 'token-backings', preset],
    queryFn: () => fetcher(`${routes.api.DISCOVERY_TOKEN_BACKINGS}?preset=${preset}`),
  });

  const items: MultiselectItem[] = useMemo(
    () => (backings ?? []).map((backing) => ({ value: backing, label: toLabel(backing) })),
    [backings],
  );
  const isUnderlyingAssetFilter = preset === 'commodities' || preset === 'stocks';

  useEffect(() => setSelectedBackings(filters.backings), [filters.backings]);

  if (!items.length && !isLoading) {
    return null;
  }

  return (
    <Multiselect
      items={items}
      value={isLoading ? undefined : selectedBackings}
      onChange={(values) => setSelectedBackings(values)}
      onDropdownClose={() => onSelectionComplete(selectedBackings)}
      modal={true}
      triggerVariant="secondary"
      label={isUnderlyingAssetFilter ? commoditiesLabel : defaultLabel}
      multiselect={true}
      search={{
        emptyLabel: isUnderlyingAssetFilter ? 'No underlying assets found' : 'No backings found',
      }}
      isLoading={isLoading}
    />
  );
};
